/**
 * Web Share Target landing page.
 *
 * Android's share sheet POSTs multipart/form-data to this URL. The service
 * worker intercepts that POST, extracts the URL, and redirects here with
 * `?url=…&title=…&text=…` query params. We then call /research/share which
 * does the OG fetch + insert and we redirect into the inbox.
 *
 * Falls back gracefully if hit directly (no params) — just bounces home.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';

export function Shared() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState<'sending' | 'done' | 'error'>('sending');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const url = (params.get('url') ?? '').trim();
    const title = params.get('title') ?? undefined;
    const text = params.get('text') ?? undefined;
    // Some share sources put the URL in `text` instead of `url`
    let chosen = url;
    if (!chosen && text) {
      const m = text.match(/https?:\/\/[^\s]+/);
      if (m) chosen = m[0];
    }
    if (!chosen) {
      // Nothing to do — bounce to inbox
      nav('/', { replace: true });
      return;
    }
    void (async () => {
      try {
        await api.post('/research/share', { url: chosen, title, text });
        setPhase('done');
        // Brief celebration then jump to inbox
        setTimeout(() => nav('/', { replace: true }), 800);
      } catch (e) {
        setPhase('error');
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [params, nav]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center space-y-3">
      {phase === 'sending' && (
        <>
          <div className="h-10 w-10 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          <p className="text-muted">Saving to research…</p>
        </>
      )}
      {phase === 'done' && (
        <>
          <CheckCircle2 className="h-10 w-10 text-accent" />
          <p>Saved.</p>
        </>
      )}
      {phase === 'error' && (
        <>
          <AlertTriangle className="h-10 w-10 text-danger" />
          <p className="text-danger text-sm break-all">{err}</p>
          <button onClick={() => nav('/', { replace: true })} className="btn-ghost py-3 px-6">
            Open inbox
          </button>
        </>
      )}
    </div>
  );
}
